// components/Admin/PDFViewer.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabaseClient';
import { 
  FileText, Upload, Trash2, Download, Eye, Search, 
  Calendar, User, Loader, AlertCircle, X,
  ChevronLeft, ChevronRight, File, FolderOpen, RefreshCw,
  Wrench, Folder, Car, Plus, Edit2, Check
} from 'lucide-react';

const PDFViewer = () => {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [selectedBucket, setSelectedBucket] = useState('tous');
  const [selectedFolder, setSelectedFolder] = useState('tous');
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFolder, setUploadFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [folders, setFolders] = useState([]);
  const itemsPerPage = 12;

  const BUCKETS = [
    { id: 'emargements-pdf', name: 'Émargements', color: 'blue', allowFolders: false },
    { id: 'interventions-pdf', name: 'Interventions', color: 'purple', allowFolders: false },
    { id: 'client-documents', name: 'Documents Clients', color: 'green', allowFolders: true }
  ];

  useEffect(() => {
    loadPDFs();
  }, [selectedBucket, selectedFolder]);

  const listAllFilesRecursive = async (bucketId, path = '', allFiles = []) => {
    try {
      const { data: files, error } = await supabase
        .storage
        .from(bucketId)
        .list(path, {
          limit: 1000,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error || !files) return allFiles;

      for (const file of files) {
        const fullPath = path ? `${path}/${file.name}` : file.name;

        if (file.name === '.emptyFolderPlaceholder') continue;

        if (file.id === null) {
          await listAllFilesRecursive(bucketId, fullPath, allFiles);
        } else {
          allFiles.push({
            ...file,
            fullPath: fullPath,
            folderPath: path || '/'
          });
        }
      }

      return allFiles;
    } catch (err) {
      console.error(`Erreur dans listAllFilesRecursive:`, err);
      return allFiles;
    }
  };

  const loadPDFs = async () => {
    try {
      setLoading(true);
      setError(null);

      let allPdfs = [];
      let allFolders = new Set();

      const bucketsToLoad = selectedBucket === 'tous' 
        ? BUCKETS.map(b => b.id)
        : [selectedBucket];

      for (const bucketId of bucketsToLoad) {
        try {
          const allFiles = await listAllFilesRecursive(bucketId);

          const pdfFiles = allFiles.filter(file => {
            const isPdf = file.name?.toLowerCase().endsWith('.pdf');
            if (isPdf && file.folderPath !== '/') {
              allFolders.add(`${bucketId}:${file.folderPath}`);
            }
            return isPdf;
          });

          const pdfsWithUrls = pdfFiles.map((file) => {
            const { data: urlData } = supabase
              .storage
              .from(bucketId)
              .getPublicUrl(file.fullPath);

            return {
              id: `${bucketId}_${file.fullPath}`,
              name: file.name,
              fullPath: file.fullPath,
              folderPath: file.folderPath,
              size: file.metadata?.size || 0,
              createdAt: file.created_at || new Date().toISOString(),
              updatedAt: file.updated_at || new Date().toISOString(),
              url: urlData.publicUrl,
              bucket: bucketId,
              bucketName: BUCKETS.find(b => b.id === bucketId)?.name || bucketId,
              bucketColor: BUCKETS.find(b => b.id === bucketId)?.color || 'gray'
            };
          });

          allPdfs = [...allPdfs, ...pdfsWithUrls];

        } catch (err) {
          console.error(`Erreur bucket ${bucketId}:`, err);
        }
      }

      allPdfs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      // Extraire les dossiers uniques pour le bucket client-documents
      const uniqueFolders = Array.from(allFolders)
        .filter(f => f.startsWith('client-documents:'))
        .map(f => f.replace('client-documents:', ''))
        .sort();

      setFolders(uniqueFolders);
      setPdfs(allPdfs);

      if (allPdfs.length === 0) {
        setError('Aucun fichier PDF trouvé. Uploadez un PDF pour commencer.');
      }

    } catch (err) {
      console.error('Erreur générale:', err);
      setError(`Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadToFolder = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Veuillez sélectionner un fichier PDF valide');
      return;
    }

    if (!uploadFolder) {
      alert('Veuillez sélectionner ou créer un dossier');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const cleanFileName = file.name
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/_{2,}/g, '_');

      const fileName = `${uploadFolder}/${Date.now()}_${cleanFileName}`;

      const { data, error: uploadError } = await supabase
        .storage
        .from('client-documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'application/pdf'
        });

      if (uploadError) throw uploadError;

      await loadPDFs();
      setShowUploadModal(false);
      setUploadFolder('');
      alert('PDF uploadé avec succès !');
    } catch (err) {
      console.error('Erreur upload:', err);
      setError(err.message);
      alert(`Erreur d'upload: ${err.message}`);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleUploadSimple = async (event, bucketId) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Veuillez sélectionner un fichier PDF valide');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const cleanFileName = file.name
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/_{2,}/g, '_');

      const fileName = `${Date.now()}_${cleanFileName}`;

      const { data, error: uploadError } = await supabase
        .storage
        .from(bucketId)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'application/pdf'
        });

      if (uploadError) throw uploadError;

      await loadPDFs();
      alert('PDF uploadé avec succès !');
    } catch (err) {
      console.error('Erreur upload:', err);
      setError(err.message);
      alert(`Erreur d'upload: ${err.message}`);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const createNewFolder = async () => {
    if (!newFolderName.trim()) {
      alert('Veuillez entrer un nom de dossier');
      return;
    }

    const cleanFolderName = newFolderName
      .trim()
      .replace(/[^a-zA-Z0-9._-\s]/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_{2,}/g, '_');

    try {
      setIsCreatingFolder(true);

      // Créer un fichier placeholder pour créer le dossier
      const placeholderPath = `${cleanFolderName}/.emptyFolderPlaceholder`;
      const { error } = await supabase
        .storage
        .from('client-documents')
        .upload(placeholderPath, new Blob([''], { type: 'text/plain' }), {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      await loadPDFs();
      setUploadFolder(cleanFolderName);
      setNewFolderName('');
      alert(`Dossier "${cleanFolderName}" créé avec succès !`);
    } catch (err) {
      console.error('Erreur création dossier:', err);
      alert(`Erreur: ${err.message}`);
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleDelete = async (pdf) => {
    if (!confirm(`Supprimer le fichier "${pdf.name}" ?\n\nChemin: ${pdf.fullPath}`)) return;

    try {
      setLoading(true);

      const { error: deleteError } = await supabase
        .storage
        .from(pdf.bucket)
        .remove([pdf.fullPath]);

      if (deleteError) throw deleteError;

      await loadPDFs();
      if (selectedPdf?.id === pdf.id) {
        setSelectedPdf(null);
      }
      alert('PDF supprimé avec succès !');
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError(err.message);
      alert(`Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (pdf) => {
    try {
      const { data, error } = await supabase
        .storage
        .from(pdf.bucket)
        .download(pdf.fullPath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdf.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur téléchargement:', err);
      alert(`Erreur: ${err.message}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const filteredPdfs = pdfs.filter(pdf => {
    const matchesSearch = pdf.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pdf.fullPath.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFolder = selectedFolder === 'tous' || pdf.folderPath === selectedFolder;
    
    return matchesSearch && matchesFolder;
  });

  const totalPages = Math.ceil(filteredPdfs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPdfs = filteredPdfs.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedBucket, selectedFolder]);

  const getBucketIcon = (bucketColor) => {
    if (bucketColor === 'blue') return FileText;
    if (bucketColor === 'purple') return Wrench;
    if (bucketColor === 'green') return User;
    return FileText;
  };

  const getFolderIcon = (folderName) => {
    if (folderName.match(/[A-Z]{2}-\d{3}-[A-Z]{2}/i)) return Car;
    return User;
  };

  return (
    <div className="px-4 md:px-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Documents PDF
            </h1>
            <p className="text-gray-400">
              Gérez vos documents PDF ({filteredPdfs.length} fichier{filteredPdfs.length > 1 ? 's' : ''})
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={loadPDFs}
              disabled={loading}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </motion.button>

            {BUCKETS.map((bucket) => (
              bucket.allowFolders ? (
                <motion.button
                  key={bucket.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowUploadModal(true)}
                  disabled={uploading}
                  className="px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2 text-sm text-white"
                  style={{
                    background: 'linear-gradient(to right, #059669, #047857)',
                    opacity: uploading ? 0.5 : 1
                  }}
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">{bucket.name}</span>
                  <span className="sm:hidden">Clients</span>
                </motion.button>
              ) : (
                <motion.label
                  key={bucket.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative cursor-pointer text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2 text-sm"
                  style={{
                    background: bucket.color === 'blue' 
                      ? 'linear-gradient(to right, #2563eb, #1d4ed8)'
                      : 'linear-gradient(to right, #9333ea, #7e22ce)',
                    opacity: uploading ? 0.5 : 1,
                    pointerEvents: uploading ? 'none' : 'auto'
                  }}
                >
                  {uploading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Upload...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span className="hidden sm:inline">{bucket.name}</span>
                      <span className="sm:hidden">{bucket.name.split(' ')[0]}</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => handleUploadSimple(e, bucket.id)}
                    disabled={uploading}
                    className="hidden"
                  />
                </motion.label>
              )
            ))}
          </div>
        </div>

        {/* Filtres */}
        <div className="flex flex-col gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un PDF..."
              className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-3 pl-12 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filtres buckets */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setSelectedBucket('tous');
                setSelectedFolder('tous');
              }}
              className={`px-4 py-3 rounded-xl font-semibold transition-all whitespace-nowrap ${
                selectedBucket === 'tous'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'bg-slate-800 text-gray-400 hover:text-white'
              }`}
            >
              Tous
            </motion.button>
            {BUCKETS.map((bucket) => (
              <motion.button
                key={bucket.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSelectedBucket(bucket.id);
                  setSelectedFolder('tous');
                }}
                className="px-4 py-3 rounded-xl font-semibold transition-all text-white whitespace-nowrap"
                style={
                  selectedBucket === bucket.id
                    ? { 
                        background: bucket.color === 'blue' 
                          ? '#2563eb' 
                          : bucket.color === 'purple'
                          ? '#9333ea'
                          : '#059669'
                      }
                    : { background: '#1e293b', color: '#9ca3af' }
                }
              >
                <span className="hidden sm:inline">{bucket.name}</span>
                <span className="sm:hidden">{bucket.name.split(' ')[0]}</span>
              </motion.button>
            ))}
          </div>

          {/* Filtres dossiers (seulement pour client-documents) */}
          {(selectedBucket === 'client-documents' || selectedBucket === 'tous') && folders.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedFolder('tous')}
                className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap flex items-center gap-2 ${
                  selectedFolder === 'tous'
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-800 text-gray-400 hover:text-white'
                }`}
              >
                <FolderOpen className="w-4 h-4" />
                Tous les dossiers
              </motion.button>
              {folders.map((folder) => {
                const FolderIcon = getFolderIcon(folder);
                return (
                  <motion.button
                    key={folder}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedFolder(folder)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap flex items-center gap-2 ${
                      selectedFolder === folder
                        ? 'bg-green-600 text-white'
                        : 'bg-slate-800 text-gray-400 hover:text-white'
                    }`}
                  >
                    <FolderIcon className="w-4 h-4" />
                    {folder}
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* Erreur */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3"
        >
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-red-400 font-semibold mb-1">Erreur</h3>
            <p className="text-red-300 text-sm">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            <X className="w-5 h-5" />
          </button>
        </motion.div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader className="w-8 h-8 text-blue-500 animate-spin mb-4" />
          <p className="text-gray-400">Chargement des PDFs...</p>
        </div>
      ) : filteredPdfs.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-20"
        >
          <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-400 mb-2">
            Aucun PDF trouvé
          </h3>
          <p className="text-gray-500">
            {searchTerm 
              ? 'Essayez une autre recherche' 
              : 'Uploadez un fichier PDF pour commencer'
            }
          </p>
        </motion.div>
      ) : (
        <>
          {/* Grille de PDFs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
            {paginatedPdfs.map((pdf, index) => {
              const BucketIcon = getBucketIcon(pdf.bucketColor);
              const FolderIcon = getFolderIcon(pdf.folderPath);
              
              return (
                <motion.div
                  key={pdf.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -5 }}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-blue-500/50 transition-all group"
                >
                  <div className="relative bg-red-500/10 rounded-xl p-4 mb-4 flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                    <File className="w-12 h-12 text-red-400" />
                    <span 
                      className="absolute -top-2 -right-2 px-2 py-1 rounded-full text-xs font-bold text-white flex items-center gap-1"
                      style={{
                        background: pdf.bucketColor === 'blue' 
                          ? '#2563eb' 
                          : pdf.bucketColor === 'purple'
                          ? '#9333ea'
                          : '#059669'
                      }}
                    >
                      <BucketIcon className="w-3 h-3" />
                      <span className="hidden lg:inline">{pdf.bucketName}</span>
                    </span>
                  </div>

                  <h3 className="text-white font-semibold mb-1 truncate" title={pdf.name}>
                    {pdf.name}
                  </h3>

                  {pdf.folderPath !== '/' && (
                    <div className="flex items-center gap-1 text-xs text-green-400 mb-2 truncate" title={pdf.folderPath}>
                      <FolderIcon className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate font-medium">{pdf.folderPath}</span>
                    </div>
                  )}

                  <div className="text-sm text-gray-400 space-y-1 mb-4">
                    <p>{formatFileSize(pdf.size)}</p>
                    <p>{new Date(pdf.createdAt).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</p>
                  </div>

                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedPdf(pdf)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      Voir
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleDownload(pdf)}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleDelete(pdf)}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </motion.button>

              <span className="text-gray-400 px-4">
                Page {currentPage} sur {totalPages}
              </span>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </motion.button>
            </div>
          )}
        </>
      )}

      {/* Modal Upload avec gestion de dossiers */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowUploadModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 rounded-2xl w-full max-w-2xl p-6 shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <Upload className="w-7 h-7 text-green-400" />
                  Uploader un document client
                </h2>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Sélection ou création de dossier */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sélectionner un dossier existant
                  </label>
                  <select
                    value={uploadFolder}
                    onChange={(e) => setUploadFolder(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">-- Choisir un dossier --</option>
                    {folders.map((folder) => {
                      const FolderIcon = getFolderIcon(folder);
                      return (
                        <option key={folder} value={folder}>
                          {folder}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="text-center text-gray-400 text-sm">OU</div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Créer un nouveau dossier (nom du client ou immatriculation)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder="ex: Jean_Dupont ou AB-123-CD"
                      className="flex-1 bg-slate-800 border border-slate-700 rounded-xl py-3 px-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={createNewFolder}
                      disabled={isCreatingFolder || !newFolderName.trim()}
                      className="px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-xl transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {isCreatingFolder ? (
                        <Loader className="w-5 h-5 animate-spin" />
                      ) : (
                        <Plus className="w-5 h-5" />
                      )}
                    </motion.button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Les caractères spéciaux seront remplacés par des underscores
                  </p>
                </div>
              </div>

              {/* Upload du fichier */}
              {uploadFolder && (
                <div className="border-t border-slate-700 pt-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sélectionner le fichier PDF
                  </label>
                  <motion.label
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="relative cursor-pointer block w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all text-center"
                  >
                    {uploading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader className="w-5 h-5 animate-spin" />
                        Upload en cours...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Upload className="w-5 h-5" />
                        Choisir un fichier PDF
                      </span>
                    )}
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={handleUploadToFolder}
                      disabled={uploading}
                      className="hidden"
                    />
                  </motion.label>
                  <p className="text-sm text-gray-400 mt-2 text-center">
                    Le fichier sera uploadé dans: <span className="text-green-400 font-semibold">{uploadFolder}</span>
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal PDF */}
      <AnimatePresence>
        {selectedPdf && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPdf(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 rounded-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            >
              <div className="bg-slate-800 border-b border-slate-700 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-red-400" />
                  <div>
                    <h3 className="text-white font-semibold">{selectedPdf.name}</h3>
                    <p className="text-sm text-gray-400 flex items-center gap-2">
                      <span>{selectedPdf.bucketName}</span>
                      {selectedPdf.folderPath !== '/' && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1 text-green-400">
                            {React.createElement(getFolderIcon(selectedPdf.folderPath), { className: "w-3 h-3" })}
                            {selectedPdf.folderPath}
                          </span>
                        </>
                      )}
                      <span>•</span>
                      <span>{formatFileSize(selectedPdf.size)}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDownload(selectedPdf)}
                    className="p-2 bg-green-600 hover:bg-green-700 rounded-lg text-white"
                  >
                    <Download className="w-5 h-5" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedPdf(null)}
                    className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>

              <div className="flex-1 overflow-hidden">
                <iframe
                  src={selectedPdf.url}
                  className="w-full h-full"
                  title={selectedPdf.name}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PDFViewer;